import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import {
  searchServiceProviders,
  sendEmail,
  sendVerificationMail,
} from "../../api";
import { setNotification } from "../../redux/notificationSlice";
import { FORM_FIELDS, FORM_TEXTS } from "../../constants";
import { LeftBanner } from "../../layout/LeftBanner";
import { NavBar } from "../../layout/NavBar";
import { SearchFormExternal } from "../forms/SearchFormExternal";
import { SearchFormInternal } from "../forms/SearchFormInternal";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import { mergeResponse } from "./helper/mergeResponse";
import { SearchVendorResult } from "./SearchVendorResult";

export const SearchVendor = () => {
  const [emailModal, setEmailModal] = useState({
    requestId: "",
    show: false,
    message: "",
    email: "",
    type: "",
    name: "",
  });
  const [verifiedData, setVerifiedData] = useState(null);
  const [externalError, setExternalError] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const navigate = useNavigate();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.profile);
  const userType = userInfo ? "internal" : "external";

  const submitForm = async (data) => {
    const isInternalUser = userType === "internal";
    const isExternalUser = userType === "external";
    if (isInternalUser && !validateInternalData(data)) {
      setInternalError(true);
      return;
    }
    setInternalError(false);
    if (isExternalUser) {
      if (data.primaryEmail === "" && data.secondaryEmail === "") {
        setExternalError(true);
        return;
      } else {
        setExternalError(false);
      }
    }
    const transformedData = {
      ...data,
      servicesProvided: data?.servicesProvided?.map((item) => item.value),
      serviceCountries: data?.serviceCountries?.map((item) => item.value),
    };
    const requestData = {
      operation: "searchServiceProvider",
      userType: userType,
      request: { ...transformedData },
    };
    const searchResp = await searchServiceProviders(requestData);
    if (searchResp?.responseCode === 404) {
      setVerifiedData(null);
      dispatch(
        setNotification({
          type: "warning",
          show: true,
          message:
            "Warning! No record found. Please change your search criteria.",
        })
      );
      return;
    }
    const response = searchResp?.responseData;
    if (response?.length === 0) {
      dispatch(
        setNotification({
          type: "warning",
          show: true,
          message:
            "Warning! No record found. Please change your search criteria.",
        })
      );
      return;
    }

    if (isInternalUser) {
      const mergedResp = mergeResponse(response);
      setVerifiedData(mergedResp);
    } else {
      handleSearchResponse(response, data);
    }
  };

  const handleSearchResponse = (response, data) => {
    setVerifiedData(response[0]);
    if (!response[0]["isPrimaryEmailVerified"] && data.primaryEmail !== "") {
      showEmailModal("primary", response[0]);
      return;
    }
    if (
      !response[0]["isSecondaryEmailVerified"] &&
      data.secondaryEmail !== ""
    ) {
      showEmailModal("secondary", response[0]);
      return;
    }
    navigate("/update-serviceprovider", { state: response });
  };

  const showEmailModal = (emailType, response) => {
    const emailField =
      emailType === "primary" ? "primaryEmail" : "secondaryEmail";
    setEmailModal({
      requestId: response.requestId,
      show: true,
      message: `Please verify your ${emailType} email address to update service provider`,
      email: response[emailField],
      type: emailType,
      name: response.primaryName,
    });
  };

  const validateInternalData = (data) => {
    return !(
      data.servicesProvided === undefined &&
      data.serviceCountries === undefined &&
      data.serviceProviderName === ""
    );
  };

  const createEmailBody = (userName, verificationLink, type) => {
    const emailBody = `<div className="bg-gray-100 p-8 font-sans">
        <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
          <p className="mb-4">Dear ${userName},</p>
          <p className="mb-4">
            Please verify your ${type} email address using below link to update service provider:
          </p>
          <div className="text-center mb-6">
            <a
              href=${verificationLink}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              ${verificationLink}
            </a>
          </div> 
          <p className="mb-4">
            This link will be valid for 30 minutes
          </p>
          <p className="mb-4">
            Thanks!</br>
            MSD GCTO Service Portal
          </p>
        </div>
      </div>`;
    return emailBody;
  };

  const verifyEmail = async (emailObj) => {
    setEmailModal({
      requestId: "",
      show: false,
      message: "",
      email: "",
      type: "",
      name: "",
    });
    const ServiceProviderData = {
      operation: "createEmailToken",
      request: {
        requestId: emailObj.requestId,
        email: emailObj.email,
        type: emailObj.type,
      },
    };
    const resp = await sendVerificationMail(ServiceProviderData);
    if (resp?.responseData?.token) {
      const emailBody = createEmailBody(
        emailObj.name,
        `${process.env.REACT_APP_BASE_URL}/verify-email/${resp.responseData.id}/${emailObj.type}/${resp.responseData.token}`,
        emailObj.type
      );
      const verifyEmailSubject =
        "[MSD GCTO Service Provider Portal] Verify Email";
      const result = await sendEmail(verifyEmailSubject, emailBody, [
        emailObj.email,
      ]);
      if (result === 204) {
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: `Verification link sent to your ${emailObj.type} email id. Open that link to verify your email`,
          })
        );
      } else {
        dispatch(
          setNotification({
            type: "warning",
            show: true,
            message: `Unexpected server response. Please try again in sometime`,
          })
        );
      }
    }
  };

  return (
    <>
      <NavBar />
      <div className="flex mr-4">
        <div className="flex-none w-64">
          <LeftBanner />
        </div>
        <div className="flex-1 pr-24">
          <div className="w-full">
            <h3 className="font-normal text-2xl text-teal-600">
              {userInfo ? FORM_TEXTS.SEARCH_RECORD : FORM_TEXTS.UPDATE_RECORD}
            </h3>
            <form className="my-5" onSubmit={handleSubmit(submitForm)}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-5">
                {userInfo && (
                  <SearchFormInternal register={register} control={control} />
                )}

                {!userInfo && (
                  <SearchFormExternal register={register} errors={errors} />
                )}
              </div>
              {internalError && (
                <span className="text-red-500 text-sm grid grid-cols-1 sm:grid-cols-2 gap-6 -mt-5">
                  Please select atlease one search criteria
                </span>
              )}
              {externalError && (
                <span className="text-red-500 text-sm grid grid-cols-1 sm:grid-cols-2 gap-6 -my-5">
                  <span>{""}</span>Either primary or secondary email is required
                </span>
              )}
              <div className="flex w-1/6 justify-between">
                <button
                  type="button"
                  className="bg-teal-600 text-white text-center py-1 text-lg px-4 font-normal my-3"
                  onClick={() => navigate("/")}
                >
                  {FORM_FIELDS.BTN_CANCEL}
                </button>
                <button
                  type="submit"
                  className="bg-sky-800 text-white text-center py-1 text-lg px-4 font-normal my-3"
                >
                  {FORM_FIELDS.BTN_SEARCH}
                </button>
              </div>
            </form>
            {userType === "external" ? (
              <div className="text-gray-500">
                Note:{" "}
                <ul className="list-disc ml-5">
                  <li>
                    (<span className="text-red-500 font-bold text-lg">*</span>)
                    All fields are mandatory.
                  </li>
                  <li>
                    (<span className="text-red-500 font-bold text-lg">**</span>)
                    At least one of the fields is required.
                  </li>
                </ul>
              </div>
            ) : (
              <div className="text-gray-500">
                Note:{" "}
                <ul className="list-disc ml-5">
                  <li>
                    (<span className="text-red-500 font-bold text-lg">**</span>)
                    At least one of the fields is required.
                  </li>
                </ul>
              </div>
            )}

            <ConfirmationModal
              isOpen={emailModal.show}
              onClose={() =>
                setEmailModal({
                  requestId: "",
                  show: false,
                  message: "",
                  email: "",
                  type: "",
                  name: "",
                })
              }
              onDelete={() => verifyEmail(emailModal)}
              header="Verify Email address"
              message={emailModal.message}
              confirmBtn="Verify Now"
              cancelBtn="Verify Later"
            />
            {userType === "internal" && verifiedData && (
              <SearchVendorResult searchResult={verifiedData} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
